import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { capitalize, gte, isEmpty, startCase } from 'lodash/fp';
import {
  ChangeEventHandler,
  Fragment,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import { components, OptionProps } from 'react-select';
import { change, Field } from 'redux-form';
import styled from 'styled-components';

import { NEW_FIELD_KEY_LABEL } from 'dashboard/apps/InquiryTemplateEditor/AddFieldPanel/AddFieldPanel';
import { InputLabel } from 'dashboard/apps/InquiryTemplateEditor/FieldPanel/FieldPanel';
import useEditorSubPath from 'dashboard/apps/InquiryTemplateEditor/hooks/useEditorSubPath';
import {
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  selector,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import FormComponentOptionsEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/FormComponentOptionsEditor';
import InputMaskedTextFieldConfigComponentEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/InputMaskedTextFieldConfigComponentEditor';
import { ComponentFieldReference } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentPanel';
import { COMPONENT_FIELD_DEFAULTS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import { SelectedComponentAndStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { createDedupedFieldKey } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import {
  createFieldSchema,
  getNameForStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { uiStepAllComponentConfigs } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import Divider from 'dashboard/components/Divider';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InputField from 'dashboard/components/FormControls/InputField';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { RootState } from 'dashboard/redux/store';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ReferencesTooltip from 'design-system/components/Tooltip';
import { FontWeight } from 'design-system/variables/text';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import {
  DynamicOptionsFieldSchema,
  FIELD_SCHEMA_TYPE_DISPLAY,
  FieldSchema,
  FieldSchemaChoices,
  FieldSchemaType,
  getDynamicFieldOptionValueSchema,
  isDynamicFieldOption,
  isFieldSchemaWithChoices,
  STRING_SANITIZATION_OPTIONS,
} from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import {
  FieldKey,
  InputSelectComponentConfig,
  isUiFormComponentConfig,
  isUiFormFieldDynamicOptionComponentConfig,
  isUiFormFieldOptionComponentConfig,
  isUiFormMultiFieldComponentConfig,
  Option as ComponentOption,
  UiComponentConfigType,
  UiFormComponentConfig,
} from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;

const CREATE_NEW_FIELD_OPTION = 'form-component-fields-editor__create-new-field-option';
const CONFIGURE_MANUALLY_SOURCE_OPTION = 'form-component-fields-editor__configure-manually-option';

const FieldSectionTitle = styled(Paragraph).attrs({
  size: 'sm',
})`
  margin-bottom: ${Spacing[1]};
`;

const FieldToUpdateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableLink = styled(Link)`
  font-size: 12px;
`;

const ComponentFieldWrapper = styled(FieldWrapper)`
  margin-bottom: ${Spacing[3]};
`;

type FormComponentFieldsEditorProps<C extends UiFormComponentConfig> = {
  title?: string;
  fieldKey?: FieldKey<C>;
  componentReferences: ComponentFieldReference[];
  currentLocale: InquiryIso6391LanguageCode;
  editable: boolean;
  step: InquiryTemplateVersionStepUi;
  basePath: string;
  isNewField: boolean;
  fieldSchema: FieldSchema[];
  offsetElementId: string;
  autoFocus?: boolean;
  component: C;
  setSelectedComponentAndStep: (selection: SelectedComponentAndStep) => void;
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
};

export const FormComponentFieldsEditor = <C extends UiFormComponentConfig>({
  title,
  fieldKey,
  componentReferences,
  currentLocale,
  editable,
  step,
  basePath,
  component,
  isNewField,
  fieldSchema,
  offsetElementId,
  autoFocus,
  handleUpdateComponentConfig,
  setSelectedComponentAndStep,
}: FormComponentFieldsEditorProps<C>) => {
  const dispatch = useDispatch();
  const editorBasePath = useEditorSubPath({ editorSubPath: 'screens', onlyBasePath: true });

  const schemaValueIndex = fieldSchema.findIndex(
    (schema) =>
      schema.key ===
      (isUiFormMultiFieldComponentConfig(component)
        ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          component.config[fieldKey]
        : component.config.field),
  );

  const fieldName = `field-schema.${schemaValueIndex}`;
  const fieldType = isUiFormMultiFieldComponentConfig(component)
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      COMPONENT_FIELD_DEFAULTS[component.type][fieldKey].config.type
    : COMPONENT_FIELD_DEFAULTS[component.type].type;
  const currentField = schemaValueIndex !== -1 ? fieldSchema[schemaValueIndex] : null;
  const [fieldStateValue, setFieldStateValue] = useState<FieldSchema | null>(currentField);

  useEffect(() => {
    setFieldStateValue(currentField);
  }, [currentField]);

  const isSelfServeDynamicOptionsEnabled = useFeatureFlag(
    'dashboard:ite-self-serve-dynamic-options',
  );
  const fieldKeyOption =
    'field-key-options' in component.config && component.config['field-key-options'];
  const dynamicOptionSource =
    isUiFormFieldDynamicOptionComponentConfig(component) &&
    fieldSchema.find(
      (field) =>
        field.key === (component as InputSelectComponentConfig).config['field-key-options'],
    );
  const dynamicOptionValueSchemaType =
    fieldKeyOption &&
    dynamicOptionSource &&
    getDynamicFieldOptionValueSchema(dynamicOptionSource as DynamicOptionsFieldSchema)?.type;

  const fieldKeys = fieldSchema.map(({ key }) => key);
  const onFieldNameUpdate = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setFieldStateValue((prev) => {
        return produce(prev, (draftValue) => {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          draftValue.label = e.target.value;
          if (isNewField) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            draftValue.key = createDedupedFieldKey(fieldKeys, e.target.value, step.name);
          }
        });
      });
    },
    [fieldKeys, isNewField, step.name],
  );

  const dispatchFieldNameChange = useCallback(() => {
    batch(() => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      dispatch(change(form, `${fieldName}.label`, fieldStateValue.label));
      // don't update the field key if it's a new field or if there are other references
      // Decided to punt on batching the label change to all field key changes
      // Non-trivial to calculate the basePath for all referenced components
      if (isNewField && componentReferences.length <= 1) {
        // update field key in schema and referencing component config
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        dispatch(change(form, `${fieldName}.key`, fieldStateValue.key));
        if (fieldStateValue)
          handleUpdateComponentConfig({ [fieldKey as string]: fieldStateValue.key });
      }
    });
  }, [
    fieldKey,
    fieldName,
    fieldStateValue,
    isNewField,
    handleUpdateComponentConfig,
    dispatch,
    componentReferences.length,
  ]);

  const handleReferencedFieldChange = useCallback(
    (newFieldKey: string) => {
      if (newFieldKey === '' || newFieldKey === currentField?.key) return;
      const createNewField = newFieldKey === CREATE_NEW_FIELD_OPTION;
      let newReferencedField: FieldSchema | undefined = fieldSchema.find(
        ({ key }) => key === newFieldKey,
      );
      if (!createNewField && newReferencedField == null) {
        captureMessage(
          `Attempted to update referenced field for component ${component.name} to key ${newFieldKey} but no field schema was found.`,
          'warning',
        );
        return;
      }
      if (createNewField) {
        newReferencedField = createFieldSchema({
          config: component,
          fieldAttributeKey: fieldKey,
          fieldKey: createDedupedFieldKey(
            fieldKeys,
            component.type,
            getNameForStep(step).toLowerCase(),
          ),
        });
      }
      batch(() => {
        // update the field key in the component config
        const componentConfigUpdates: Record<string, any> = {
          [fieldKey as string]: newReferencedField?.key,
        };
        if (
          isUiFormFieldOptionComponentConfig(component) &&
          isFieldSchemaWithChoices(newReferencedField)
        ) {
          // update the referencing component's options to match new field's option values
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const updatedOptions: Array<ComponentOption> = newReferencedField.config.options.map(
            (option) => {
              return {
                value: option,
                'text-translations': [
                  {
                    locale: 'en-US',
                    value: titleCase(option),
                  },
                ],
              };
            },
          );
          componentConfigUpdates['options'] = updatedOptions;
        }
        handleUpdateComponentConfig(componentConfigUpdates);
        // update field schema if needed to add new field or remove unsaved field from component drop
        if (isNewField || createNewField) {
          const updatedFieldSchema = createNewField
            ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              fieldSchema.concat(newReferencedField)
            : fieldSchema.filter((field) => field.key !== currentField?.key);
          dispatch(change(form, 'field-schema', updatedFieldSchema));
        }
      });
    },
    [
      currentField?.key,
      fieldSchema,
      component,
      fieldKey,
      fieldKeys,
      step,
      isNewField,
      dispatch,
      handleUpdateComponentConfig,
    ],
  );

  const validateMaxLength = useCallback(
    (value: number) => {
      if (currentField == null || currentField.type !== FieldSchemaType.String) return;
      const maxCharLength = currentField?.config['max-char-length'];
      return gte(maxCharLength, value) ? undefined : `Max length must be ${maxCharLength} or less`;
    },
    [currentField],
  );

  const createFieldSchemaFromDynamicOptionSource = (
    dynamicOptionSource: DynamicOptionsFieldSchema,
  ) => {
    return produce(getDynamicFieldOptionValueSchema(dynamicOptionSource), (draftField) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftField.key = createDedupedFieldKey(
        fieldKeys,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        `Input Select ${draftField.type}`,
        getNameForStep(step).toLowerCase(),
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftField.label = startCase(draftField.key);
    });
  };

  const handleOptionSourceChange = useCallback(
    (sourceFieldKey: string) => {
      if (sourceFieldKey === CONFIGURE_MANUALLY_SOURCE_OPTION) {
        handleReferencedFieldChange(CREATE_NEW_FIELD_OPTION);
        // clear field key options if the source is changed to configure manually
        handleUpdateComponentConfig({ 'field-key-options': null });
      } else {
        const dynamicOptionSource = fieldSchema.find((field) => field.key === sourceFieldKey);
        // creates a new field schema by copying the dynamic field's value schema
        const newValueFieldSchema = createFieldSchemaFromDynamicOptionSource(
          dynamicOptionSource as DynamicOptionsFieldSchema,
        );
        // add the new field to the field schema
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        dispatch(change(form, 'field-schema', fieldSchema.concat(newValueFieldSchema)));
        // update the field key in the component config
        handleUpdateComponentConfig({
          [fieldKey as string]: newValueFieldSchema?.key,
          'field-key-options': sourceFieldKey,
        });
      }
    },
    [
      handleReferencedFieldChange,
      dispatch,
      handleUpdateComponentConfig,
      fieldSchema,
      createFieldSchemaFromDynamicOptionSource,
      fieldKey,
    ],
  );

  const handleKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const displayFieldType = (type: string | undefined) => {
    return (
      <Select size='sm' id='component-fields-editor__field-type-select' value={type} disabled>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <Option value={type}>{capitalize(type)}</Option>
      </Select>
    );
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const referencedFieldOptions = useMemo<FieldSelectOptionType[]>(() => {
    const createNewFieldOption = {
      label: 'Create new field',
      value: CREATE_NEW_FIELD_OPTION,
      dynamicOptionValueSchemaType,
    };
    const fieldOptions = fieldSchema
      .filter((field) => field.type === fieldType || fieldKeyOption)
      .map(({ key, label, type }) => ({
        label: currentField?.key === key && isNewField ? `${label ?? ''} (New field)` : label,
        value: key,
        dynamicOptionValueSchemaType,
        disabled: fieldKeyOption && type !== dynamicOptionValueSchemaType,
      }));
    return !isNewField && !fieldKeyOption ? [createNewFieldOption, ...fieldOptions] : fieldOptions;
  }, [
    dynamicOptionValueSchemaType,
    currentField?.key,
    fieldKeyOption,
    fieldSchema,
    fieldType,
    isNewField,
  ]);

  const showDynamicOption =
    isSelfServeDynamicOptionsEnabled && component.type === UiComponentConfigType.InputSelect;
  const showOptions = isUiFormFieldOptionComponentConfig(component) && schemaValueIndex !== -1;

  const currentReferences = componentReferences.filter(
    ({ componentName }) => component.name !== componentName,
  );

  return (
    <>
      {title && <FieldSectionTitle>{title}</FieldSectionTitle>}
      {showOptions && (
        <>
          <Label size='lg' weight='normal'>
            Options
          </Label>
          <Spacer height={2} />
          {showDynamicOption && (
            <ComponentFieldWrapper>
              <InputLabel>Source</InputLabel>
              <Select
                size='sm'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value={fieldKeyOption ?? CONFIGURE_MANUALLY_SOURCE_OPTION}
                onValueChange={handleOptionSourceChange}
                disabled={!editable}
              >
                <Option value={CONFIGURE_MANUALLY_SOURCE_OPTION}>Configure Manually</Option>
                {fieldSchema.filter(isDynamicFieldOption).map((field) => (
                  <Option key={field.key} value={field.key}>
                    {field.label}
                  </Option>
                ))}
              </Select>
              <Spacer height={1} />
            </ComponentFieldWrapper>
          )}
          {showDynamicOption && fieldKeyOption ? (
            <>
              <Label>Source Type</Label>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {displayFieldType(dynamicOptionValueSchemaType)}
            </>
          ) : (
            <FormComponentOptionsEditor
              options={component.config.options}
              fieldName={fieldName}
              componentName={component.name}
              handleUpdateComponentConfig={handleUpdateComponentConfig}
              currentField={currentField as FieldSchemaChoices}
              offsetElementId={offsetElementId}
              currentLocale={currentLocale}
              disabled={!editable}
            />
          )}
          <Divider full />
        </>
      )}
      <FieldToUpdateHeader>
        <Label size='lg' weight='normal'>
          Field to update
        </Label>
        {currentField && (
          <TableLink to={`${editorBasePath}/config/fields/${currentField.key}`}>
            View in fields table
          </TableLink>
        )}
      </FieldToUpdateHeader>
      {currentReferences.length > 0 && (
        <FieldReferencesLabel
          currentReferences={currentReferences}
          setSelectedComponentAndStep={setSelectedComponentAndStep}
        />
      )}
      <Spacer height={2} />
      <ComponentFieldWrapper>
        <InputLabel htmlFor='component-fields-editor__field-select'>Field</InputLabel>
        <ReactSelect
          id='component-fields-editor__field-select'
          aria-label='Select referenced field'
          placeholder='No field referenced'
          value={currentField?.key ?? ''}
          options={referencedFieldOptions}
          components={{ Option: FieldSelectOption }}
          onChange={handleReferencedFieldChange}
          disabled={!editable}
          isSearchable
        />
      </ComponentFieldWrapper>
      {fieldStateValue != null && (
        <>
          <ComponentFieldWrapper>
            <FormGroup>
              <FormControlLabel>
                <InputLabel htmlFor='component-fields-editor__field-label-input'>
                  Field Label
                </InputLabel>
                <HelpIcon>
                  {`This sets the label of the field to update. The component's display label can be edited on the Properties tab.`}
                </HelpIcon>
              </FormControlLabel>
              <Input
                size='sm'
                type='text'
                id='component-fields-editor__field-label-input'
                value={fieldStateValue?.label}
                hasError={isEmpty(fieldStateValue?.label)}
                disabled={!editable}
                autoFocus={autoFocus}
                onChange={onFieldNameUpdate}
                onBlur={dispatchFieldNameChange}
                onKeyPress={handleKeyPress}
              />
            </FormGroup>
          </ComponentFieldWrapper>
          <ComponentFieldWrapper>
            <FormGroup>
              <FormControlLabel>
                <InputLabel htmlFor='component-fields-editor__field-key-input'>
                  Field Key
                </InputLabel>
              </FormControlLabel>
              <Input
                size='sm'
                type='text'
                id='component-fields-editor__field-key-input'
                value={fieldStateValue?.key}
                disabled
              />
            </FormGroup>
            {isNewField && (
              <>
                <Spacer height={1} />
                <Paragraph>{NEW_FIELD_KEY_LABEL}</Paragraph>
              </>
            )}
          </ComponentFieldWrapper>
          <FormControlLabel>
            <InputLabel htmlFor='component-fields-editor__field-type-select'>Field Type</InputLabel>
          </FormControlLabel>
          {displayFieldType(currentField?.type)}
          <Spacer height={1} />
          {fieldType === FieldSchemaType.String && (
            <>
              <ComponentFieldWrapper>
                <Field
                  size='sm'
                  type='number'
                  id='component-fields-editor__field-max-char-input'
                  component={InputField}
                  name={`${fieldName}.config.max-char-length`}
                  label={
                    <InputLabel htmlFor='component-fields-editor__field-max-char-input'>
                      Max length (char)
                    </InputLabel>
                  }
                  validate={validateMaxLength}
                  parse={parseInt}
                  disabled={schemaValueIndex === -1 || !editable}
                  showError
                />
              </ComponentFieldWrapper>
              <ComponentFieldWrapper>
                <Field
                  id='component-fields-editor__field-string-sanitization-input'
                  component={ReactSelectField}
                  name={`${fieldName}.config.sanitize`}
                  label={
                    <InputLabel htmlFor='component-fields-editor__field-string-sanitization-input'>
                      String sanitization
                    </InputLabel>
                  }
                  options={STRING_SANITIZATION_OPTIONS}
                  disabled={!editable}
                  isMulti
                />
              </ComponentFieldWrapper>
            </>
          )}
          {component.type === UiComponentConfigType.InputMaskedText && (
            <InputMaskedTextFieldConfigComponentEditor
              basePath={`steps.${basePath}`}
              disabled={!editable}
            />
          )}
        </>
      )}
    </>
  );
};

type FieldSelectOptionType = OptionType & {
  dynamicOptionValueSchemaType: FieldSchemaType | undefined;
};

export const FieldSelectOption = (props: OptionProps<FieldSelectOptionType>) => {
  const { label, value, dynamicOptionValueSchemaType, disabled } = props.data;
  const option = (
    <components.Option key={value} {...props} innerProps={{ ...props.innerProps }}>
      {label}
    </components.Option>
  );
  return disabled ? (
    <Tooltip
      trigger='hover'
      placement='top-start'
      delayShow={200}
      tooltip={
        <Paragraph color='primary' size='sm'>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {`Field type must be "${FIELD_SCHEMA_TYPE_DISPLAY[dynamicOptionValueSchemaType]}" to match the source field's type`}
        </Paragraph>
      }
    >
      {option}
    </Tooltip>
  ) : (
    option
  );
};

const CountContainer = styled.div`
  display: inline-flex;
  column-gap: 2px;
  svg {
    margin-bottom: ${Spacing[1]};
  }
`;

const ReferenceCount = styled.span`
  color: ${({ theme }) => theme.color.text.primary};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
`;

const ReferencesContainer = styled.div`
  line-height: ${Spacing[5]};
`;

const ReferenceLinkButton = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.color.primary.brand600};
`;

const FieldReferencesLabel = ({
  currentReferences,
  setSelectedComponentAndStep,
}: {
  currentReferences: ComponentFieldReference[];
  setSelectedComponentAndStep: (selection: SelectedComponentAndStep) => void;
}) => {
  const steps = useSelector<RootState, InquiryTemplateVersionStep[]>(
    (state) => selector(state, 'steps') ?? [],
  );
  const handleSetSelected = useCallback(
    (stepName: string, componentName: string) => {
      const step = steps.filter(isUiStepConfig).find(({ name }) => name === stepName);
      const component = uiStepAllComponentConfigs(step)
        .filter(isUiFormComponentConfig)
        .find(({ name }) => name === componentName);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSelectedComponentAndStep({ step, component, error: false });
    },
    [steps, setSelectedComponentAndStep],
  );
  const componentLabel = currentReferences.length > 1 ? 'components' : 'component';
  return (
    <>
      <Spacer height={1} />
      <Label size='sm' weight='normal'>
        Referenced by
      </Label>{' '}
      <ReferencesTooltip
        side='top'
        trigger={
          <CountContainer>
            <ReferenceCount>{`${currentReferences.length} other ${componentLabel}`}</ReferenceCount>
            <CircleInfoIcon size={12} />
          </CountContainer>
        }
      >
        <ReferencesContainer>
          {currentReferences.map(({ componentName, stepName, displayLabel }) => (
            <Fragment key={componentName}>
              <ReferenceLinkButton
                id={`component-fields-editor__${stepName}-${componentName}-link-button`}
                onClick={() => handleSetSelected(stepName, componentName)}
              >
                {displayLabel}
              </ReferenceLinkButton>
              <br />
            </Fragment>
          ))}
        </ReferencesContainer>
      </ReferencesTooltip>
    </>
  );
};
